import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import API_BASE_URL from './config/api';
import { DASHBOARD_ACCESS_GROUPS } from './maintenanceAccess';
import './InternalUserAccessEditor.css';

const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('managerToken') || ''}`, 'Content-Type': 'application/json' });
const sameTabs = (a, b) => a.length === b.length && a.every(tab => b.includes(tab));
const validAccess = data => data && Array.isArray(data.hiddenTabs) && Array.isArray(data.roleAllowedTabs) && Array.isArray(data.allowedTabs);

function CategoryCheckbox({ mixed, ...props }) {
  const input = useRef(null);
  useEffect(() => { if (input.current) input.current.indeterminate = mixed; }, [mixed]);
  return <input ref={input} type="checkbox" aria-checked={mixed ? 'mixed' : props.checked} {...props} />;
}

const InternalUserAccessEditor = forwardRef(function InternalUserAccessEditor({ userId, roleChanged = false, onDirtyChange, onSavingChange }, ref) {
  const [source, setSource] = useState(null);
  const [hiddenTabs, setHiddenTabs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [reload, setReload] = useState(0);
  const endpoint = `${API_BASE_URL}/api/maintenance/access/users/${encodeURIComponent(userId)}`;
  const dirty = Boolean(source && !sameTabs(hiddenTabs, source.hiddenTabs));

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true); setSource(null); setHiddenTabs([]); setError('');
    (async () => {
      try {
        const response = await fetch(endpoint, { headers: headers(), signal: controller.signal });
        const data = await response.json();
        if (!response.ok || !validAccess(data) || data.canManage !== true) throw new Error(data.error || 'Dashboard access could not be loaded.');
        if (!controller.signal.aborted) { setSource(data); setHiddenTabs(data.hiddenTabs); }
      } catch (err) {
        if (!controller.signal.aborted) setError(err.message || 'Dashboard access could not be loaded.');
      } finally { if (!controller.signal.aborted) setLoading(false); }
    })();
    return () => controller.abort();
  }, [endpoint, reload]);

  useEffect(() => { onDirtyChange?.(dirty); }, [dirty, onDirtyChange]);
  useEffect(() => { onSavingChange?.(saving); }, [saving, onSavingChange]);
  useEffect(() => { if (roleChanged && source) setHiddenTabs(source.hiddenTabs); }, [roleChanged, source]);
  const roleAllowed = useMemo(() => new Set(source?.roleAllowedTabs || []), [source]);
  const protectedTabs = useMemo(() => new Set(source?.protectedTabs || []), [source]);

  function toggle(keys, visible) {
    setHiddenTabs(previous => {
      const next = new Set(previous);
      keys.forEach(key => {
        if (protectedTabs.has(key)) return;
        if (visible) next.delete(key); else next.add(key);
      });
      return [...next].sort();
    });
    setError('');
  }

  const save = useCallback(async () => {
    if (!dirty) return true;
    if (loading || saving || roleChanged || !source?.canManage) return false;
    setSaving(true); setError('');
    try {
      const response = await fetch(endpoint, { method: 'PUT', headers: headers(), body: JSON.stringify({ hiddenTabs }) });
      const data = await response.json();
      if (!response.ok || !validAccess(data)) throw new Error(data.error || 'Dashboard access could not be saved.');
      setSource(data); setHiddenTabs(data.hiddenTabs);
      onDirtyChange?.(false);
      window.dispatchEvent(new Event('maintenanceAccessChanged'));
      return true;
    } catch (err) {
      setError(err.message || 'Dashboard access could not be saved.');
      return false;
    } finally { setSaving(false); }
  }, [dirty, endpoint, hiddenTabs, loading, onDirtyChange, roleChanged, saving, source]);
  useImperativeHandle(ref, () => ({ save, isDirty: () => dirty }), [save, dirty]);

  return <section className="internal-user-access" aria-label="Dashboard access">
    <div className="internal-user-access__heading">
      <div><h3>Dashboard access</h3><p>Choose the categories and tabs this user can see. Their role still sets the maximum access.</p></div>
      {source && <button type="button" disabled={saving || roleChanged || !hiddenTabs.length} onClick={() => { setHiddenTabs([]); setError(''); }}>Reset to role defaults</button>}
    </div>
    {source?.storageMode === 'local' && <p className="internal-user-access__hint">Local preview · Access changes here do not affect the live dashboard.</p>}
    {roleChanged && <p className="internal-user-access__hint">Save the role change first, then reopen this user to adjust dashboard access.</p>}
    {error && <div className="internal-user-access__error" role="alert">{error}{!source && <button type="button" onClick={() => setReload(value => value + 1)}>Retry</button>}</div>}
    {loading ? <p role="status">Loading dashboard access…</p> : source && <fieldset disabled={saving || roleChanged}>
      {DASHBOARD_ACCESS_GROUPS.map(group => {
        const available = group.tabs.filter(tab => roleAllowed.has(tab.key));
        const visible = available.filter(tab => !hiddenTabs.includes(tab.key));
        const editable = available.filter(tab => !protectedTabs.has(tab.key));
        const mixed = visible.length > 0 && visible.length < available.length;
        const standalone = group.key === 'today';
        return <div className={`internal-user-access__group${standalone ? ' internal-user-access__standalone' : ''}`} key={group.key}>
          <label className="internal-user-access__category"><CategoryCheckbox aria-label={standalone ? `${group.label}: ${group.tabs[0].label}` : `${group.label} category`} checked={available.length > 0 && visible.length === available.length} mixed={mixed} disabled={!editable.length} onChange={event => toggle(group.tabs.map(tab => tab.key), event.target.checked)} /><strong>{group.label}</strong><span>{visible.length}/{available.length} visible</span></label>
          {!standalone && <div className="internal-user-access__tabs">{group.tabs.map(tab => {
            const allowed = roleAllowed.has(tab.key), locked = protectedTabs.has(tab.key);
            return <label key={tab.key} className={!allowed ? 'internal-user-access__locked' : ''}><input type="checkbox" aria-label={`${group.label}: ${tab.label}`} checked={allowed && !hiddenTabs.includes(tab.key)} disabled={!allowed || locked} onChange={event => toggle([tab.key], event.target.checked)} /><span>{tab.label}{!allowed && <small>Not available for this role</small>}{locked && <small>Required for your own admin access</small>}</span></label>;
          })}</div>}
        </div>;
      })}
    </fieldset>}
    {dirty && <p className="internal-user-access__hint">Use Save changes below to apply these selections.</p>}
  </section>;
});

export default InternalUserAccessEditor;
